/**
 * One home for each report that used to live in several places in the sidebar (owner, 2026-10-29: the same report
 * in more than one place confused the office staff).
 *
 * A hub is an existing page (its `home`) that now shows the other pages of its group as tabs. Each tab keeps its own
 * page id, so its permission, صلاحيات المستخدمين and إعدادات الإظهار still decide who sees it; the tab pages are off
 * the sidebar, and their old address opens the hub on their tab with the query kept (router.tsx).
 *
 * Pure data: the sidebar tree imports it, so it must not import the tree.
 */
export interface PageHubTab {
  key: string
  ar: string
  en: string
  /** The page ids this tab stands for, in order; the first one this person may open is the screen shown. */
  pages: string[]
}

export interface PageHub {
  /** The page id whose address is the hub; its own screen is the first tab. */
  home: string
  path: string
  tabs: PageHubTab[]
}

/** The query key that names the open tab. */
export const HUB_TAB_PARAM = 'tab'

export const PAGE_HUBS: PageHub[] = [
  {
    home: 'analysis.branches', path: '/analysis/branches',
    tabs: [
      { key: 'analysis', ar: 'تحليل الفروع', en: 'Branch analysis', pages: ['analysis.branches'] },
      { key: 'collections', ar: 'تحصيلات الفروع', en: 'Branch collections', pages: ['reports.branches'] },
      { key: 'profit', ar: 'أرباح الفروع', en: 'Branch gross profit', pages: ['branches.performance'] },
    ],
  },
  {
    home: 'analysis.clients', path: '/analysis/clients',
    tabs: [
      { key: 'analysis', ar: 'تحليل العملاء', en: 'Client analysis', pages: ['analysis.clients'] },
      // تقارير العملاء and لوحة العملاء were the same screen (CollectionReview/GetByClients) under two names
      { key: 'collections', ar: 'تحصيلات العملاء', en: 'Client collections', pages: ['reports.clients', 'clients.dashboard'] },
    ],
  },
  {
    home: 'analysis.pickups', path: '/analysis/pickups',
    tabs: [
      { key: 'analysis', ar: 'تحليل البيك أب', en: 'Pickup analysis', pages: ['analysis.pickups'] },
      { key: 'requests', ar: 'طلبات البيك أب', en: 'Pickup requests', pages: ['pickup.requestsTrack'] },
      { key: 'runners', ar: 'الاستلامات مع المناديب', en: 'With the runners', pages: ['pickup.tracking'] },
    ],
  },
  {
    home: 'finance.commission', path: '/finance/commission',
    tabs: [
      { key: 'single', ar: 'شحنة واحدة', en: 'One shipment', pages: ['finance.commission'] },
      { key: 'bulk', ar: 'عدة شحنات أو ملف Excel', en: 'Several shipments or an Excel sheet', pages: ['finance.commissionEdit'] },
    ],
  },
  {
    home: 'operations.weights', path: '/operations/weights',
    tabs: [
      { key: 'weigh', ar: 'وزن الشحنات', en: 'Weigh shipments', pages: ['operations.weights'] },
      { key: 'edit', ar: 'تعديل الوزن', en: 'Edit weight', pages: ['operations.weight'] },
    ],
  },
  {
    home: 'hr.employees', path: '/hr/employees',
    tabs: [
      { key: 'hr', ar: 'ملفات الموظفين', en: 'Employee files', pages: ['hr.employees'] },
      { key: 'dispatch', ar: 'موظفو Dispatch', en: 'Dispatch employees', pages: ['admin.employees'] },
    ],
  },
]

const BY_HOME = new Map(PAGE_HUBS.map((h) => [h.home, h]))
/** Every page that became a tab of a hub (the homes themselves are not in it). */
export const HUB_TAB_PAGES: ReadonlySet<string> = new Set(PAGE_HUBS.flatMap((h) => h.tabs.flatMap((t) => t.pages)).filter((id) => !BY_HOME.has(id)))

export const hubOfHome = (pageId: string): PageHub | undefined => BY_HOME.get(pageId)

/** The hub and tab a page id now lives in, if it is part of a hub. */
export function hubTabOf(pageId: string): { hub: PageHub; tab: PageHubTab } | null {
  for (const hub of PAGE_HUBS) for (const tab of hub.tabs) if (tab.pages.includes(pageId)) return { hub, tab }
  return null
}

/** The hub address for one of its tabs, keeping the rest of the query (?awb=…). */
export function hubUrl(hub: PageHub, tabKey: string, search = ''): string {
  const q = new URLSearchParams(search)
  q.delete(HUB_TAB_PARAM)
  const rest = q.toString()
  return `${hub.path}?${HUB_TAB_PARAM}=${encodeURIComponent(tabKey)}${rest ? `&${rest}` : ''}`
}

/** Where the old address of a tab page goes now; null for a page that is not a tab (or is a hub's home). */
export function redirectForTabPage(pageId: string, search = ''): string | null {
  if (!HUB_TAB_PAGES.has(pageId)) return null
  const found = hubTabOf(pageId)
  return found ? hubUrl(found.hub, found.tab.key, search) : null
}

/**
 * The tabs of a hub this person may open, each with the page it shows. `canOpen` is the same check the sidebar and
 * the page guard use (permission, صلاحيات المستخدمين, إعدادات الإظهار).
 */
export function openTabs<P extends { id: string }>(hub: PageHub, byId: ReadonlyMap<string, P>, canOpen: (page: P) => boolean): (PageHubTab & { page: P })[] {
  const out: (PageHubTab & { page: P })[] = []
  for (const tab of hub.tabs) {
    const page = tab.pages.map((id) => byId.get(id)).find((p): p is P => Boolean(p) && canOpen(p!))
    if (page) out.push({ ...tab, page })
  }
  return out
}
